import { FC, Fragment, useEffect, useState } from "react";
import {
    Backdrop, Box, Button, CircularProgress, FormControl, Grid, InputLabel, Select, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, TextField, styled, tableCellClasses
} from "@mui/material";
import { useStyles } from "../styles";
import CheckAttr from "../../../utils/CheckAttr";
import { DataRef } from "../../../types/DataRef";
import { AxiosInstance } from "axios";
import { SnackBarAlert } from "../../element/SnackBarAlert";
import { AlertType } from "../../../types/AlertType";
import {
    PROFIL_ADV, PROFIL_COUNTRY_MANAGER, PROFIL_MANAGER, PROFIL_ROA, PROFIL_SUPER_ADMIN,
    PROFIL_SUPER_MANAGER
} from "../../../constants/Constants";
import { ConnectedUser } from "../../../utils/utils";

interface Row {
    pays_id: number;
    pays_libelle: string;
    bu_id: number;
    bu_libelle: string;
    client_id: number;
    client_libelle: string;
    operation_id: number;
    operation_libelle: string;
    article_id: number;
    article_libelle: string;
    code_article: string;
    devise: string;
    tarif: string;
    acte: string;
    heure_ca: string;
    ca: string;
    cout_activite: string;
    heure_production: string;
    cout: string;
    marge: string;
    taux_marge: string;
    status?: string;
}

interface CumulData {
    pays: number;
    bu: number;
    client: number;
    operation: number;
    date_debut: string;
    date_fin: string;
    rows: Row[];
    has_unvalidated?: boolean;
    all_validated?: boolean;
    has_only_validated?: boolean;
}

interface CumulArticleProps {
    dataPays: DataRef[];
    http: AxiosInstance;
}

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover
    },
    // hide last border
    '&:last-child td, &:last-child th': {
        border: 0
    },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: "#080d24",
        color: theme.palette.common.white,
        fontSize: 11,
        textAlign: "center",
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 11,
        textAlign: "center"
    },
}));

const CumulArticle: FC<CumulArticleProps> = ({ dataPays, http }) => {
    const classes = useStyles();

    const user = ConnectedUser();
    const profil = user?.profils?.libelle;

    const [loader, setLoader] = useState<boolean>(false);
    const [actionLoader, setActionLoader] = useState<boolean>(false);
    const [openSnack, setOpenSnack] = useState<boolean>(false);
    const [alertMessage, setAlerteMessage] = useState<string>("");
    const [alertType, setAlertType] = useState<AlertType>("success");
    const [triggerReload, setTriggerReload] = useState<boolean>(false);

    /**
     * La validation du cumul est ouverte à la production : manager, chef de plateau
     * (SUPER_MANAGER) et ROA (profils COUNTRY_MANAGER et ROA).
     */
    const showValidateBtn = [
        PROFIL_SUPER_ADMIN, PROFIL_MANAGER, PROFIL_SUPER_MANAGER, PROFIL_COUNTRY_MANAGER, PROFIL_ROA
    ].includes(profil);
    const showSendBtn = [PROFIL_SUPER_ADMIN, PROFIL_ADV].includes(profil);

    const handleCloseAlert = () => {
        setOpenSnack(false);
    };

    const handleUpdateStatus = async (status: 'validée' | 'envoyée') => {
        if (!CheckAttr(paramCumul?.pays) ||
            !CheckAttr(paramCumul?.bu) ||
            !CheckAttr(paramCumul?.client) ||
            !CheckAttr(paramCumul?.operation) ||
            !CheckAttr(paramCumul?.date_debut) ||
            !CheckAttr(paramCumul?.date_fin)) {
            setAlerteMessage("Veuillez sélectionner tous les critères de filtrage obligatoires.");
            setAlertType("warning");
            setOpenSnack(true);
            return;
        }

        setActionLoader(true);
        try {
            const response = await http.post(`saisie/manager/update-status-by-criteria`, {
                pays: paramCumul?.pays,
                bu: paramCumul?.bu,
                client: paramCumul?.client,
                operation: paramCumul?.operation,
                date_debut: paramCumul?.date_debut,
                date_fin: paramCumul?.date_fin,
                status: status
            });
            const message = (status === "validée") ? "Les saisies ont été verouillé avec succès !" : "Les saisies ont été clôturé avec succès !";
            setAlerteMessage(response.data.message || message);
            setAlertType("success");
            setOpenSnack(true);
            setTriggerReload(prev => !prev);
        } catch (error: any) {
            console.error(error);
            const errorMsg = error?.response?.data?.message || "Une erreur est survenue lors de la mise à jour des statuts.";
            setAlerteMessage(errorMsg);
            setAlertType("error");
            setOpenSnack(true);
        } finally {
            setActionLoader(false);
        }
    };

    const [dataBusinessUnits, setDataBusinessUnits] = useState<DataRef[]>([]);
    const [dataClients, setDataClients] = useState<DataRef[]>([]);
    const [dataOperations, setDataOperations] = useState<DataRef[]>([]);

    const [paramCumul, setParamCumul] = useState<CumulData>();

    const handleChange = (e: any) => {
        const { name, value } = e.target;

        setParamCumul({
            ...paramCumul as CumulData,
            [name]: value
        });
    };

    /**
     * Recupère le BU par rapport au pays
     * @param pays
     */
    const getBusinessUnits = async (pays: any) => {
        await http
            .get(`saisie/manager/get-bus/${pays}`)
            .then((res) => {
                Array.isArray(res.data) && setDataBusinessUnits(res.data);
            })
            .catch((error) => {
                console.log(error);
            });
    };

    /**
     * Recupère les clients actifs par rapport au pays et BU
     * (actif=1 : les clients désactivés sont exclus du cumul)
     */
    const getClients = async () => {
        await http
            .get(`saisie/manager/get-clients/${paramCumul?.pays}/${paramCumul?.bu}?actif=1`)
            .then((res) => {
                Array.isArray(res.data) && setDataClients(res.data);
            })
            .catch((error) => {
                console.log(error);
            });
    };


    /**
     * Recupère les operations actives par rapport au pays, BU et client
     * (actif : les opérations désactivées sont exclues du cumul)
     */
    const getOperations = async () => {
        await http
            .post(`saisie/manager/get-operations`, {
                pays: paramCumul?.pays,
                bu: paramCumul?.bu,
                client: paramCumul?.client,
                actif: true,
            })
            .then((res) => {
                Array.isArray(res.data) && setDataOperations(res.data);
            })
            .catch((error) => {
                console.log(error);
            });
    };

    useEffect(() => {
        if (CheckAttr(paramCumul?.pays)) {
            getBusinessUnits(paramCumul?.pays);
        } else {
            setDataBusinessUnits([]);
        }
    }, [paramCumul?.pays]);

    useEffect(() => {
        if (CheckAttr(paramCumul?.bu)) {
            getClients();
        } else {
            setDataClients([]);
        }
    }, [paramCumul?.bu]);

    useEffect(() => {
        if (CheckAttr(paramCumul?.client)) {
            getOperations();
        } else {
            setDataOperations([]);
        }
    }, [paramCumul?.client]);

    useEffect(() => {
        if (CheckAttr(paramCumul?.pays) &&
            CheckAttr(paramCumul?.bu) &&
            CheckAttr(paramCumul?.client) &&
            CheckAttr(paramCumul?.operation) &&
            CheckAttr(paramCumul?.date_debut) &&
            CheckAttr(paramCumul?.date_fin)) {
            setLoader(true);
            http.post(`saisie/manager/get-cumule`, {
                pays: paramCumul?.pays,
                bu: paramCumul?.bu,
                client: paramCumul?.client,
                operation: paramCumul?.operation,
                date_debut: paramCumul?.date_debut,
                date_fin: paramCumul?.date_fin
            }).then((res) => {
                if (res.data && Array.isArray(res.data.rows)) {
                    setParamCumul({
                        ...paramCumul as CumulData,
                        rows: res.data.rows as Row[],
                        has_unvalidated: res.data.has_unvalidated,
                        all_validated: res.data.all_validated,
                        has_only_validated: res.data.has_only_validated
                    });
                }
            }).catch((error) => {
                console.log(error);
            }).finally(() => {
                setLoader(false);
            });
        } else {
            setParamCumul({
                ...paramCumul as CumulData,
                rows: [],
                has_unvalidated: false,
                all_validated: false,
                has_only_validated: false
            });
        }
    }, [
        paramCumul?.pays,
        paramCumul?.bu,
        paramCumul?.client,
        paramCumul?.operation,
        paramCumul?.date_debut,
        paramCumul?.date_fin,
        triggerReload
    ]);

    const showTable = (paramCumul?.rows && paramCumul?.rows?.length > 0) && true;

    const totals = (paramCumul?.rows ?? []).reduce((acc, row) => ({
        ca: acc.ca + (Number(row?.ca) || 0),
        acte: acc.acte + (Number(row?.acte) || 0),
        heure_ca: acc.heure_ca + (Number(row?.heure_ca) || 0),
        heure_production: acc.heure_production + (Number(row?.heure_production) || 0),
        cout: acc.cout + (Number(row?.cout) || 0),
        marge: acc.marge + (Number(row?.marge) || 0),
    }), { ca: 0, acte: 0, heure_ca: 0, heure_production: 0, cout: 0, marge: 0 });

    const tauxMargeMoyen = totals.ca > 0 ? ((totals.marge / totals.ca) * 100).toFixed(2) : "0.00";

    /**
     * Devise(s) de facturation des articles du cumul (normalement une seule par opération)
     */
    const deviseFacturation = Array.from(
        new Set((paramCumul?.rows ?? []).map((row) => row?.devise).filter(Boolean))
    ).join(" / ");

    return (
        <Fragment>
            <Box className={classes.root} mb={5}>
                <Grid container spacing={2} columns={{ xs: 6, sm: 12, md: 12 }}>
                    <Grid item xs={6} sm={6} md={6}>
                        <FormControl
                            variant="outlined"
                            className={classes.formControl}
                            sx={{ marginLeft: 1, marginTop: 1 }}
                        >
                            <InputLabel
                                htmlFor="pays"
                                color="warning"
                                shrink={CheckAttr(paramCumul?.pays)}
                            >
                                Pays de production
                            </InputLabel>
                            <Select
                                id="pays"
                                native
                                color="warning"
                                value={paramCumul?.pays}
                                onChange={handleChange}
                                label="Pays de production"
                                inputProps={{
                                    name: "pays",
                                }}
                            >
                                <option value=""></option>
                                {dataPays.map((pays) => (
                                    <option key={pays?.id} value={pays?.id}>
                                        {pays?.libelle}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl
                            variant="outlined"
                            className={classes.formControl}
                            sx={{ marginLeft: 1, marginTop: 1 }}
                        >
                            <InputLabel
                                htmlFor="client"
                                color="warning"
                                shrink={CheckAttr(paramCumul?.client)}
                            >
                                Client
                            </InputLabel>
                            <Select
                                id="client"
                                native
                                color="warning"
                                value={paramCumul?.client}
                                onChange={handleChange}
                                label="Client"
                                inputProps={{
                                    name: "client",
                                }}
                            >
                                <option value=""></option>
                                {dataClients.map((client) => (
                                    <option key={client?.id} value={client?.id}>
                                        {client?.libelle}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            name="date_debut"
                            label="Date debut"
                            type="date"
                            color="warning"
                            fullWidth
                            value={paramCumul?.date_debut}
                            onKeyDown={(e) => e.preventDefault()}
                            onChange={handleChange}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    </Grid>

                    <Grid item xs={6} sm={6} md={6}>
                        <FormControl
                            variant="outlined"
                            className={classes.formControl}
                            sx={{ marginLeft: 1, marginTop: 1 }}
                        >
                            <InputLabel
                                htmlFor="bu"
                                color="warning"
                                shrink={CheckAttr(paramCumul?.bu)}
                            >
                                Business unit
                            </InputLabel>
                            <Select
                                id="bu"
                                native
                                color="warning"
                                value={paramCumul?.bu}
                                onChange={handleChange}
                                label="Business unit"
                                inputProps={{
                                    name: "bu",
                                }}
                            >
                                <option value=""></option>
                                {dataBusinessUnits.map((bu) => (
                                    <option key={bu?.id} value={bu?.id}>
                                        {bu?.libelle}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl
                            variant="outlined"
                            className={classes.formControl}
                            sx={{ marginLeft: 1, marginTop: 1 }}
                        >
                            <InputLabel
                                htmlFor="operation"
                                color="warning"
                                shrink={CheckAttr(paramCumul?.operation)}
                            >
                                Opération
                            </InputLabel>
                            <Select
                                id="operation"
                                native
                                color="warning"
                                value={paramCumul?.operation}
                                onChange={handleChange}
                                label="Opération"
                                inputProps={{
                                    name: "operation",
                                }}
                            >
                                <option value=""></option>
                                {dataOperations.map((operation) => (
                                    <option key={operation?.id} value={operation?.id}>
                                        {operation?.libelle}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            name="date_fin"
                            label="Date fin"
                            type="date"
                            color="warning"
                            fullWidth
                            value={paramCumul?.date_fin}
                            onKeyDown={(e) => e.preventDefault()}
                            onChange={handleChange}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>

            {showTable && (
                <>
                    <TableContainer sx={{ mt: 4 }}>
                        <Table sx={{ minWidth: 700 }} aria-label="cumul totals table">
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell>CA HT total</StyledTableCell>
                                    <StyledTableCell>Devise de facturation</StyledTableCell>
                                    <StyledTableCell>Actes total</StyledTableCell>
                                    <StyledTableCell>Heures total</StyledTableCell>
                                    <StyledTableCell>Heures de production total</StyledTableCell>
                                    <StyledTableCell>Coût total</StyledTableCell>
                                    <StyledTableCell>Marge totale</StyledTableCell>
                                    <StyledTableCell>Taux de marge moyen</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <StyledTableRow>
                                    <StyledTableCell>{totals.ca.toFixed(2)}</StyledTableCell>
                                    <StyledTableCell>{deviseFacturation}</StyledTableCell>
                                    <StyledTableCell>{totals.acte.toFixed(2)}</StyledTableCell>
                                    <StyledTableCell>{totals.heure_ca.toFixed(2)}</StyledTableCell>
                                    <StyledTableCell>{totals.heure_production.toFixed(2)}</StyledTableCell>
                                    <StyledTableCell>{totals.cout.toFixed(2)}</StyledTableCell>
                                    <StyledTableCell>{totals.marge.toFixed(2)}</StyledTableCell>
                                    <StyledTableCell>{tauxMargeMoyen} %</StyledTableCell>
                                </StyledTableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TableContainer sx={{ mt: 4 }}>
                        <Table sx={{ minWidth: 700 }} aria-label="customized table">
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell>Codes article</StyledTableCell>
                                    <StyledTableCell>Articles / Désignation</StyledTableCell>
                                    <StyledTableCell>Tarif</StyledTableCell>
                                    <StyledTableCell>Heures</StyledTableCell>
                                    <StyledTableCell>Actes</StyledTableCell>
                                    <StyledTableCell>CA HT</StyledTableCell>
                                    <StyledTableCell>Coût/h</StyledTableCell>
                                    <StyledTableCell>Heures de production</StyledTableCell>
                                    <StyledTableCell>Coût</StyledTableCell>
                                    <StyledTableCell>Marge</StyledTableCell>
                                    <StyledTableCell>Taux de marge</StyledTableCell>
                                    <StyledTableCell>Statut</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paramCumul?.rows?.map((article) => (
                                    <StyledTableRow key={article?.article_id}>
                                        <StyledTableCell>{article?.code_article}</StyledTableCell>
                                        <StyledTableCell>{article?.article_libelle}</StyledTableCell>
                                        <StyledTableCell>{article?.tarif}</StyledTableCell>
                                        <StyledTableCell>{article?.heure_ca}</StyledTableCell>
                                        <StyledTableCell>{article?.acte}</StyledTableCell>
                                        <StyledTableCell>{article?.ca}</StyledTableCell>
                                        <StyledTableCell>{article?.cout_activite}</StyledTableCell>
                                        <StyledTableCell>{article?.heure_production}</StyledTableCell>
                                        <StyledTableCell>{article?.cout}</StyledTableCell>
                                        <StyledTableCell>{article?.marge}</StyledTableCell>
                                        <StyledTableCell>{article?.taux_marge}</StyledTableCell>
                                        <StyledTableCell>{article?.status}</StyledTableCell>
                                    </StyledTableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
                )}

            {/* Partie Manager (et Super Admin pour la validation) */}
            {showValidateBtn && paramCumul?.has_unvalidated && (
                <Box display="flex" justifyContent="center" gap={2} mt={3} mb={3}>
                    <Button
                        variant="contained"
                        color="warning"
                        disabled={actionLoader || loader || !CheckAttr(paramCumul?.pays) || !CheckAttr(paramCumul?.bu) || !CheckAttr(paramCumul?.client) || !CheckAttr(paramCumul?.operation) || !CheckAttr(paramCumul?.date_debut) || !CheckAttr(paramCumul?.date_fin)}
                        onClick={() => handleUpdateStatus('validée')}
                        sx={{ textTransform: "none", minWidth: 120 }}
                    >
                        Valider
                    </Button>
                </Box>
            )}

            {/* Partie ADV */}
            {profil === PROFIL_ADV && (
                <Box display="flex" flexDirection="column" alignItems="center" gap={2} mt={3} mb={3}>
                    {paramCumul?.has_unvalidated && (
                        <span style={{ color: "#d32f2f", fontWeight: "medium", fontSize: "14px" }}>
                            Les saisies ne sont pas encore validées par la production
                        </span>
                    )}
                    {paramCumul?.has_only_validated && (
                        <Button
                            variant="contained"
                            disabled={actionLoader || loader || !CheckAttr(paramCumul?.pays) || !CheckAttr(paramCumul?.bu) || !CheckAttr(paramCumul?.client) || !CheckAttr(paramCumul?.operation) || !CheckAttr(paramCumul?.date_debut) || !CheckAttr(paramCumul?.date_fin)}
                            onClick={() => handleUpdateStatus('envoyée')}
                            sx={{
                                textTransform: "none",
                                minWidth: 120,
                                backgroundColor: "#080d24",
                                color: "#fff",
                                "&:hover": {
                                    backgroundColor: "#111a40",
                                },
                                "&.Mui-disabled": {
                                    backgroundColor: "rgba(255, 255, 255, 0.12)",
                                    color: "rgba(255, 255, 255, 0.3)"
                                }
                            }}
                        >
                            Facture envoyée au client
                        </Button>
                    )}
                </Box>
            )}

            {/* Partie Super Admin (pour l'envoi) */}
            {/* {profil === PROFIL_SUPER_ADMIN && paramCumul?.has_only_validated && (
                <Box display="flex" justifyContent="center" gap={2} mt={3} mb={3}>
                    <Button
                        variant="contained"
                        disabled={actionLoader || loader || !CheckAttr(paramCumul?.pays) || !CheckAttr(paramCumul?.bu) || !CheckAttr(paramCumul?.client) || !CheckAttr(paramCumul?.operation) || !CheckAttr(paramCumul?.date_debut) || !CheckAttr(paramCumul?.date_fin)}
                        onClick={() => handleUpdateStatus('envoyée')}
                        sx={{
                            textTransform: "none",
                            minWidth: 120,
                            backgroundColor: "#080d24",
                            color: "#fff",
                            "&:hover": {
                                backgroundColor: "#111a40",
                            },
                            "&.Mui-disabled": {
                                backgroundColor: "rgba(255, 255, 255, 0.12)",
                                color: "rgba(255, 255, 255, 0.3)"
                            }
                        }}
                    >
                        Envoyer
                    </Button>
                </Box>
            )} */}

            {SnackBarAlert(openSnack, handleCloseAlert, alertType, alertMessage)}

            <div>
                <Backdrop
                    sx={{ color: "white", zIndex: (theme) => theme.zIndex.drawer + 1 }}
                    open={loader || actionLoader}
                >
                    <CircularProgress color="inherit" />
                </Backdrop>
            </div>
        </Fragment>
    );
};

export default CumulArticle;